import { type RefObject, useLayoutEffect, useRef } from 'react';

import {
  PDF_LIST_SCROLL_MARGIN,
  PDF_PAGE_VERTICAL_PADDING,
  type PDFVirtualizedListHandle,
} from '../components/PDFPagesVirtualizedList';
import { getRealPDFScale } from '../utils/getRealPDFScale';

/**
 * This hook is used to persist scroll position in pdf when user zooms in or out
 */
export const usePersistScrollPositionOnZoom = ({
  listRef,
  currentScale,
  currentPage,
}: {
  listRef: RefObject<PDFVirtualizedListHandle>;
  currentScale: number;
  currentPage: number;
}) => {
  const prevScale = useRef(0);

  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  useLayoutEffect(() => {
    listRef.current?.updateItemsSize();
  }, [currentScale, listRef]);

  useLayoutEffect(() => {
    if (!listRef.current) return;

    if (prevScale.current !== currentScale) {
      const scrollElement = listRef.current.getScrollElement();
      if (!scrollElement) return;
      const scaleRatio = getRealPDFScale(currentScale) / getRealPDFScale(prevScale.current);

      const currentTopOffset = scrollElement.scrollTop;
      const currentLeftOffset = scrollElement.scrollLeft;

      const additionalHeight =
        PDF_LIST_SCROLL_MARGIN +
        (currentPageRef.current - 1) * 2 * PDF_PAGE_VERTICAL_PADDING +
        PDF_PAGE_VERTICAL_PADDING;

      const newTopOffset = (currentTopOffset - additionalHeight) * scaleRatio + additionalHeight;
      const newLeftOffset = currentLeftOffset * scaleRatio;

      requestAnimationFrame(() => {
        listRef.current?.getScrollElement()?.scrollTo({ top: newTopOffset, left: newLeftOffset });
      });

      prevScale.current = currentScale;
    }
  }, [currentScale, listRef]);
};
